export class UrlHelper {
  /**
   * Returns the backTo query param value if it is safe to use (same domain or same-root subdomain).
   * Returns null if the param is absent or points to an external domain.
   */
  static getSafeBackTo(
    search: string = window.location.search,
    currentOrigin: string = window.location.origin,
    currentHostname: string = window.location.hostname
  ): string | null {
    const backTo = search
      ? new URLSearchParams(search)?.get("backTo")
      : null;

    if (!backTo) return null;

    try {
      const url = new URL(backTo, currentOrigin);
      if (!UrlHelper.isSameDomain(url, currentHostname)) return null;
    } catch {
      return null;
    }

    return backTo;
  }

  /**
   * Returns true if the given URL belongs to the same hostname or any subdomain
   * of the current page's root domain (e.g. portal.mydomain.com is allowed from login.mydomain.com).
   */
  static isSameDomain(url: URL, currentHostname: string = window.location.hostname): boolean {
    const rootDomain = UrlHelper.getRootDomain(currentHostname);
    return (
      url.hostname === currentHostname ||
      url.hostname.endsWith("." + rootDomain) ||
      url.hostname === rootDomain
    );
  }

  /**
   * The registrable root domain of a hostname (its last two labels),
   * e.g. `my.levelesq.net` -> `levelesq.net`. For single-label hosts
   * (`localhost`) the value is returned unchanged.
   */
  static getRootDomain(hostname: string = window.location.hostname): string {
    return hostname.split(".").slice(-2).join(".");
  }

  /**
   * Registrable domain with a leading dot, suitable as a cross-subdomain cookie
   * `Domain` attribute (e.g. `my.levelesq.net` -> `.levelesq.net`). Returns
   * `undefined` for `localhost` / single-label hosts so a host-only cookie is
   * written in local development.
   */
  static getCookieDomain(hostname: string = window.location.hostname): string | undefined {
    if (hostname === "localhost") {
      return undefined;
    }
    const rootDomain = UrlHelper.getRootDomain(hostname);
    return rootDomain.includes(".") ? `.${rootDomain}` : undefined;
  }
}
